import { CatalogItem } from "../../../domain/catalogItem"
import { EstimatedItem } from "../../../domain/workareaItem"
import { ItemWorkareaData } from "../../Items/itemModel"
import { TemplateState } from "../../Templates/templateModel"

export type SaveAsTemplateItem = {
  enabled: boolean
  estimatedItemId: number
  catalogItemGuid: string
  name: string
  useQuantity: boolean
  quantity: number
  units?: string
  rateInstallationQuantity?: number
  rateUsageQuantity?: number
  ratePerUnits?: string
  sortIndex: number
}

export type SaveAsTemplateModel = {
  name: string
  copyInternalNotes?: boolean
  copyCustomerAndInvoiceNotes?: boolean
  copyInvoiceDescription?: boolean
  templateItems: SaveAsTemplateItem[]
}

export const defaultModel = () => ({
  name: '',
  templateItems: []
})

export const createModel = (workarea: ItemWorkareaData, estimatedItems: EstimatedItem[], catalogItems: CatalogItem[]): SaveAsTemplateModel => {
  return {
    name: workarea.name,
    copyInternalNotes: true,
    copyCustomerAndInvoiceNotes: true,
    copyInvoiceDescription: true,
    templateItems: estimatedItems.map(item => {
      const catalogItem = catalogItems.find(catalogItem => catalogItem.guid === item.catalogItemGuid)
      return ({
        enabled: true,
        estimatedItemId: item.id,
        catalogItemGuid: item.catalogItemGuid,
        name: item.name,
        useQuantity: true,
        quantity: item.quantity,
        units: catalogItem?.units,
        rateInstallationQuantity: catalogItem?.rateInstallationQuantity,
        rateUsageQuantity: catalogItem?.rateUsageQuantity,
        ratePerUnits: catalogItem?.ratePerUnits,
        sortIndex: item.sortIndex
      })
    })
  }
}

export const createChangeSet = (model: SaveAsTemplateModel, workarea: ItemWorkareaData, catalogItems: CatalogItem[]): TemplateState => {
  return {
    name: model.name,
    workareaInternalNotes: model.copyInternalNotes ? workarea.notes : undefined,
    workareaCustomerAndInvoiceNotes: model.copyCustomerAndInvoiceNotes ? workarea.customerAndInvoiceNotes : undefined,
    workareaInvoiceDescription: model.copyInvoiceDescription ? workarea.invoiceDescription ?? undefined : undefined,
    addedItems: model.templateItems
      .filter(item => item.enabled)
      .map(item => ({
        id: 0,
        name: item.name,
        catalogItemGuid: item.catalogItemGuid,
        catalogName: catalogItems.find(catalogItem => catalogItem.guid === item.catalogItemGuid)?.name ?? '',
        quantity: item.useQuantity ? item.quantity : 0,
        units: item.units ?? '',
        rateInstallationQuantity: item.rateInstallationQuantity,
        rateUsageQuantity: item.rateUsageQuantity,
        ratePerUnits: item.ratePerUnits,
        sortIndex: item.sortIndex
      })),
    updatedItems: [],
    deletedItemIds: [],
  }
}

export type Action =
  | { type: 'RESET_FORM', model: SaveAsTemplateModel }
  | { type: 'NAME_CHANGED', name: string }
  | { type: 'COPY_INTERNAL_NOTES_CHANGED', enabled: boolean }
  | { type: 'COPY_CUSTOMER_AND_INVOICE_NOTES_CHANGED', enabled: boolean }
  | { type: 'COPY_INVOICE_DESCRIPTION_CHANGED', enabled: boolean }
  | { type: 'USE_ITEM_QUANTITY_CHANGED', estimatedItemId: number, enabled: boolean }
  | { type: 'USE_ITEM_QUANTITIES_CHANGED', enabled: boolean }
  | { type: 'ITEM_TOGGLED', estimatedItemId: number, enabled: boolean }

export const reducer = (state: SaveAsTemplateModel, action: Action): SaveAsTemplateModel => {
  switch (action.type) {
    case 'RESET_FORM':
      return action.model
    case 'NAME_CHANGED':
      return { ...state, name: action.name }
    case 'COPY_INTERNAL_NOTES_CHANGED':
      return { ...state, copyInternalNotes: action.enabled }
    case 'COPY_CUSTOMER_AND_INVOICE_NOTES_CHANGED':
      return { ...state, copyCustomerAndInvoiceNotes: action.enabled }
    case 'COPY_INVOICE_DESCRIPTION_CHANGED':
      return { ...state, copyInvoiceDescription: action.enabled }
    case 'USE_ITEM_QUANTITY_CHANGED':
      return {
        ...state,
        templateItems: state.templateItems.map(item => {
          if (item.estimatedItemId === action.estimatedItemId) {
            return { ...item, useQuantity: action.enabled }
          }

          return item
        })
      }
    case 'USE_ITEM_QUANTITIES_CHANGED':
      return {
        ...state,
        templateItems: state.templateItems.map(item => ({
          ...item,
          useQuantity: action.enabled
        }))
      }
    case 'ITEM_TOGGLED':
      return {
        ...state,
        templateItems: state.templateItems.map(item => {
          if (item.estimatedItemId === action.estimatedItemId) {
            return { ...item, enabled: action.enabled }
          }

          return item
        })
      }
  }
}
